/**
 * CompanyCalendarPrint — rendu imprimable de la page « Company calendar »
 * (hr-time-company) : calendrier entreprise mensuel + synthèse TT/T0.
 *
 * Unité de contenu autonome (fetch propre + onDataReady), enveloppée dans
 * .ppl-print__section (A4 paysage, cf. PeoplePrint.css).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { getCompanyTimeYear } from '../../../api/hrTime';
import { formatNumber, formatDate } from '../../../utils/format';
import PrintReportHeader from './PrintReportHeader';

const EN_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const KIND_EN = { official: 'Holiday', offered: 'Offered leave', compensated: 'Made-up leave' };

const f0 = (v) => formatNumber(Number(v) || 0, 0);
const f1 = (v) => formatNumber(Number(v) || 0, 1);
const f2 = (v) => formatNumber(Number(v) || 0, 2);

export default function CompanyCalendarPrint({ activeTenant, year, editedAt, onDataReady }) {
  const tenantId = activeTenant?.id;
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!tenantId) return;
    getCompanyTimeYear(tenantId, year)
      .then(d => setDetail((d && d.data) ? d.data : d))
      .catch(() => {})
      .finally(() => onDataReady && onDataReady('company-calendar'));
  }, [tenantId, year]); // eslint-disable-line

  const months = useMemo(() => detail?.computed?.months || [], [detail]);
  const c = detail?.computed || {};
  const p = detail?.params || {};
  const holidays = useMemo(
    () => [...(detail?.holidays || [])].sort((a, b) => (a.holiday_date || '').localeCompare(b.holiday_date || '')),
    [detail],
  );
  const sum = (k) => months.reduce((a, m) => a + (Number(m[k]) || 0), 0);

  const daysDue = Number(c.working_days_year || 0) - Number(c.weekday_offered_year || 0);
  const netWorked = Number(c.final_working_days_year || 0);
  const hoursDue = Number(c.t0_annual_hours || 0);
  const hPerDayMakeup = netWorked > 0 ? hoursDue / netWorked : Number(c.daily_contractual_hours || 0);

  const synth = [
    ['Contractual hours / week', `${f1(p.weekly_contractual_hours)} h`],
    ['Standard h / day', `${f2(c.daily_contractual_hours)} h`],
    ['Weekdays / year', f0(c.weekday_count_year)],
    ['Weekday holidays', f0(c.weekday_holidays_year)],
    ['Gross working days', f0(c.working_days_year)],
    ['Offered leave (year)', f0(c.weekday_offered_year)],
    ['Days due', f1(daysDue), true],
    ['Made-up leave (year)', f1(c.compensated_days_year)],
    ['Net worked days', f1(netWorked), true],
    ['H / day with make-up', `${f2(hPerDayMakeup)} h`],
    ['Total worked-day hours', `${f2(c.tt_annual_hours)} h`],
    ['Total Time (TT, h)', `${f2(hoursDue)} h`, true],
  ];

  return (
    <>
    <section className="ppl-print__section">
      <PrintReportHeader category="Time" title="Company calendar" year={year} tenant={activeTenant} editedAt={editedAt} />

      {/* Synthèse (1/3) · Holidays & leaves (2/3) côte à côte */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', marginBottom: 16 }}>
        <div style={{ flex: '1 1 0', minWidth: 0 }}>
          <table className="ppl-print__table">
            <caption>Synthesis</caption>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Indicator</th>
                <th className="num">Value</th>
              </tr>
            </thead>
            <tbody>
              {synth.map(([label, value, bold]) => (
                <tr key={label}>
                  <td style={{ textAlign: 'left' }}>{label}</td>
                  <td className="num nowrap">{bold ? <strong>{value}</strong> : value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ flex: '2 1 0', minWidth: 0 }}>
          <table className="ppl-print__table">
            <caption>Holidays &amp; leaves · {holidays.length}</caption>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Date</th>
                <th style={{ textAlign: 'left' }}>Label</th>
                <th style={{ textAlign: 'left' }}>Category</th>
                <th style={{ textAlign: 'left' }}>Source</th>
              </tr>
            </thead>
            <tbody>
              {holidays.map((h, i) => (
                <tr key={h.id || i} className={i % 2 === 0 ? 'zebra' : ''}>
                  <td style={{ textAlign: 'left' }} className="nowrap">{formatDate(h.holiday_date)}</td>
                  <td style={{ textAlign: 'left' }}>{h.label}</td>
                  <td style={{ textAlign: 'left' }}>{KIND_EN[h.kind] || KIND_EN.official}</td>
                  <td style={{ textAlign: 'left' }}>{h.source === 'manual' ? 'manual' : 'cantonal'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>

    {/* ── Page 2 : Monthly detail ── */}
    <section className="ppl-print__section">
      <PrintReportHeader category="Time" title="Company calendar — Monthly detail" year={year} tenant={activeTenant} editedAt={editedAt} />
      <table className="ppl-print__table ppl-print__table--fixed ppl-print__table--dense">
        <caption>Monthly detail · {months.length} months</caption>
        <thead>
          <tr>
            <th>Month</th>
            <th className="num nowrap">Weekdays</th>
            <th className="num nowrap">Holidays</th>
            <th className="num nowrap">Working days</th>
            <th className="num nowrap">Days off</th>
            <th className="num nowrap">Made-up days</th>
            <th className="num nowrap">Worked days</th>
            <th className="num nowrap">Total Time (TT, h)</th>
          </tr>
        </thead>
        <tbody>
          {months.map((m, idx) => (
            <tr key={m.month} className={idx % 2 === 0 ? 'zebra' : ''}>
              <td>{EN_MONTHS[(m.month || 1) - 1]}</td>
              <td className="num nowrap">{f0(m.weekday_count)}</td>
              <td className="num nowrap">{f0(m.weekday_holidays)}</td>
              <td className="num nowrap">{f0(m.working_days)}</td>
              <td className="num nowrap">{f0(m.weekday_offered)}</td>
              <td className="num nowrap">{f1(m.compensated_days_off)}</td>
              <td className="num nowrap">{f1(m.working_days_net)}</td>
              <td className="num nowrap"><strong>{f2(m.working_hours_net)}</strong></td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td className="nowrap">TOTAL</td>
            <td className="num nowrap">{f0(sum('weekday_count'))}</td>
            <td className="num nowrap">{f0(sum('weekday_holidays'))}</td>
            <td className="num nowrap">{f0(sum('working_days'))}</td>
            <td className="num nowrap">{f0(sum('weekday_offered'))}</td>
            <td className="num nowrap">{f1(sum('compensated_days_off'))}</td>
            <td className="num nowrap">{f1(sum('working_days_net'))}</td>
            <td className="num nowrap"><strong>{f2(sum('working_hours_net'))}</strong></td>
          </tr>
        </tfoot>
      </table>
    </section>
    </>
  );
}
